import { Buffer } from 'buffer'

import { message } from 'antd'
import BigNumber from 'bignumber.js'
import Numeral from 'numeral'
import { useEffect, useRef, useState } from 'react'
import { keccak256 } from 'viem'
import Web3 from 'web3'

import { getDecimal } from '@/config'
import type { invalidAmount } from '@/types'
import type { ActionType } from '@/types/action-type'
import type { TokenType } from '@/types/token-type'

const web3 = new Web3()

export const monthfy = new Array(
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Spt',
  'Oct',
  'Nov',
  'Dec',
)

export function fromWei1(data: any, unit = 'ether') {
  // @ts-ignore
  return web3.utils.fromWei(data.toString(), unit)
}

export const resetRoute = () => (window.location.href = window.location.origin)

export const isFalsy = (value: unknown) => (value === 0 ? false : !value)

export const isVoid = (value: unknown) =>
  value === undefined || value === null || value === ''

export const cleanObject = (object?: { [key: string]: unknown }) => {
  if (!object) {
    return {}
  }
  const result = { ...object }
  Object.keys(result).forEach((key) => {
    const value = result[key]
    if (isVoid(value)) {
      delete result[key]
    }
  })
  return result
}

export const subset = <
  O extends { [key in string]: unknown },
  K extends keyof O,
>(
  obj: O,
  keys: K[],
) => {
  const filteredEntries = Object.entries(obj).filter(([key]) =>
    keys.includes(key as K),
  )
  return Object.fromEntries(filteredEntries) as Pick<O, K>
}

export const isInValidAmount = (value: invalidAmount) =>
  [null, undefined, '0', 0].includes(value)

export const useMount = (callback: () => void) => {
  useEffect(() => {
    callback()
  }, [])
}

/**
 * 返回组件的挂载状态，如果还没挂载或者已经卸载，返回false；反之，返回true
 */
export const useMountedRef = () => {
  const mountedRef = useRef(false)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  })

  return mountedRef
}

export const shorthandString = (str: string, len?: number): string => {
  const tmp = len || 8
  return str.substr(0, tmp / 2 + 2) + '....' + str.substr(-tmp / 2)
}

export const formatNumBetweenZeroAndOne = (str: string): string => {
  return Number(str).toFixed(6).toString()
}

// eg. 1.03000 ==> 1.03
export const removeExtraZero = (str: string): string => {
  let decimalPlace = String(parseFloat('.' + str.split('.')[1]))
  decimalPlace = new BigNumber(decimalPlace).toString(10)
  if (isNaN(Number(decimalPlace))) return str
  return Number(decimalPlace) === 0
    ? str.split('.')[0]
    : str.split('.')[0] + '.' + decimalPlace.split('.')[1]
}

export const toHexParams = (str: string, decimals?: number): string => {
  return (
    '0x' +
    new BigNumber(str)
      .multipliedBy(Math.pow(10, decimals || decimals === 0 ? decimals : 18))
      .toString(16)
  )
}

export const isValidAmount = (data: string | number | null | undefined) => {
  if (data) {
    return new BigNumber(data).gt('0')
  }
  return false
}

export const fromWei = (data: string | number | null, decimal?: number) => {
  if (!data) {
    return '0'
  }
  const ret = new BigNumber(data).dividedBy(Math.pow(10, 18)).toString(10)
  return removeExtraZero(
    Numeral(Number(ret).toFixed(decimal || 6)).format('0,0.000'),
  )
}

export const formatValue = (
  value: string | number | undefined,
  tokenType?: TokenType | 'WAN',
): string => {
  if (!value) {
    return '0'
  }
  const decimals = tokenType ? getDecimal.get(tokenType) || 0 : 0
  const formatNum = new BigNumber(value)
    .dividedBy(Math.pow(10, decimals))
    .toString(10)
  return removeExtraZero(Numeral(formatNum).format('0,0'))
}

export const toApproveBigNum = (tokenType: TokenType) => {
  const decimals = tokenType ? getDecimal.get(tokenType) || 0 : 0
  return (
    '0x' +
    new BigNumber('2', 10)
      .pow(255)
      .minus(1)
      .dividedBy('1e' + decimals)
      .integerValue()
      .toString(16)
  )
}

export const formatBalance = (
  balance: string,
  decimal: number | string = 18,
): string => {
  return new BigNumber(balance).dividedBy(Math.pow(10, +decimal)).toString(10)
}

export const formatSplitBalance = (
  balance: string,
  decimal: number | string = 18,
  len: number | string = 2,
): string => {
  let amount = new BigNumber(balance)
    .dividedBy(new BigNumber(10).pow(decimal))
    .toFixed(Number(len))
  amount = removeExtraZero(amount)
  return amount
}

export const formatBalanceAddPer = (
  balance: string,
  decimal: number | string = 18,
  len: number | string = 2,
): string => {
  const intBalance = new BigNumber(balance)
    .dividedBy(Math.pow(10, +decimal))
    .toString(10)
  const mock = `(0.${new Array(len).fill(0).join('')} a)`
  const formatStr = Numeral(intBalance).format(mock)
  const [formatNumStr, per] = formatStr.split(' ')
  const perStr = removeExtraZero(formatNumStr)
  if (isNaN(Number(perStr))) {
    return '0'
  } else {
    return per
      ? `${perStr}${['k'].includes(per) ? per : per.toLocaleUpperCase()}`
      : perStr
  }
}

export const formatBalanceAddPerLimitMax = (
  balance: string,
  decimal: number | string = 18,
  len: number | string = 2,
  maxLen: number | string = 8,
): string => {
  const intBalance = new BigNumber(balance)
    .dividedBy(Math.pow(10, +decimal))
    .toString(10)
  let limitNum = len === 2 ? decimal : len
  limitNum = Number(limitNum) > Number(maxLen) ? maxLen : limitNum
  const mock = `(0.${new Array(Number(intBalance) >= 10 ? 2 : limitNum).fill(0).join('')} a)`
  const formatStr = Numeral(intBalance).format(mock)
  const [formatNumStr, per] = formatStr.split(' ')
  const perStr = removeExtraZero(formatNumStr)
  if (isNaN(Number(perStr))) {
    return '0'
  } else {
    return per
      ? `${perStr}${['k'].includes(per) ? per : per.toLocaleUpperCase()}`
      : perStr
  }
}
export const formatUsdBalanceAddPer = (
  balance: string,
  len: number | string = 2,
): string => {
  const intBalance = new BigNumber(balance).toString(10)
  const mock = `(0.${new Array(len).fill(0).join('')} a)`
  const formatStr = Numeral(intBalance).format(mock)
  const [formatNumStr, per] = formatStr.split(' ')
  const perStr = removeExtraZero(formatNumStr)
  if (isNaN(Number(perStr))) {
    return '0'
  } else {
    return per
      ? `${perStr}${['k'].includes(per) ? per : per.toLocaleUpperCase()}`
      : perStr
  }
}

export const useLoading = () => {
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 2000)
    return () => clearTimeout(timer)
  }, [])

  return { isLoading }
}

export const validGroupId = (id: string) =>
  id !== '0x0000000000000000000000000000000000000000000000000000000000000000' &&
  !!id

interface isValidInputAmountProps {
  balance: string
  amount: string
  action: ActionType
  tokenType: TokenType
}
interface isValidInputAmountResult {
  error: boolean
  message?: string
}
export const isValidInputAmount = (
  params: isValidInputAmountProps,
): isValidInputAmountResult => {
  const { balance, amount, action, tokenType } = params

  switch (action) {
    case 'Unstake':
    case 'Withdraw':
      if (tokenType === 'WAN' && new BigNumber(balance).eq('0')) {
        return {
          error: true,
          message: 'Please make sure there is sufficient gas fee',
        }
      } else {
        return { error: false }
      }
    case 'Deposit':
      const ruleOne =
        isNaN(Number(amount)) ||
        (new BigNumber(amount).gte(balance) && tokenType === 'WAN')
      const ruleTwo =
        isNaN(Number(amount)) ||
        (new BigNumber(amount).gt(balance) && tokenType !== 'WAN')
      const ruleThree = new BigNumber(amount).lte('0')
      const ruleFour = new BigNumber(amount)
        .multipliedBy(Math.pow(10, getDecimal.get(tokenType) || 0))
        .isInteger()

      if (ruleOne || ruleTwo || ruleThree || !ruleFour) {
        return { error: true, message: 'Please enter a valid amount' }
      } else {
        return { error: false }
      }
    default:
      return { error: false }
  }
}

export const cutString = (str: string, len: number): string => {
  if (str.length * 2 <= len) {
    return str
  }
  var strlen = 0
  var s = ''
  for (var i = 0; i < str.length; i++) {
    s = s + str.charAt(i)
    if (str.charCodeAt(i) > 128) {
      strlen = strlen + 2
      if (strlen >= len) {
        return (
          s.substring(0, s.length - 1) + '...' + str.substring(str.length - 4)
        )
      }
    } else {
      strlen = strlen + 1
      if (strlen >= len) {
        return (
          s.substring(0, s.length - 2) + '...' + str.substring(str.length - 4)
        )
      }
    }
  }
  return s
}

const formatHMS1 = (cur: Date) => {
  var h = cur.getUTCHours()
  var m: number | string = cur.getMinutes()
  if (m < 10) m = '0' + m
  var s: number | string = cur.getSeconds()
  if (s < 10) s = '0' + s
  if (h < 10) {
    return '0' + h + ':' + m
  } else {
    return h + ':' + m
  }
}

export const getUTCV2 = (time: number, onlyDate = false) => {
  const current = new Date(time * 1000)
  const mon = current.getUTCMonth() + 1
  const date = current.getUTCDate()
  const year = current.getUTCFullYear()
  const hms = formatHMS1(current)
  if (onlyDate) {
    return `${year}-${mon}-${date}`
  } else {
    return `${year}-${mon}-${date} ${hms}`
  }
}

export const hexCharCodeToStr = (str: string) => {
  str = str.trim().replace(/^0x/g, '')
  if (str.length % 2 !== 0) {
    return ''
  }
  let tempstr = ''
  try {
    for (var b = 0; b < str.length; b = b + 2) {
      if (str.substring(b, b + 2) === '00') {
        continue
      }
      tempstr =
        tempstr + String.fromCharCode(parseInt(str.substring(b, b + 2), 16))
    }
  } catch {
    tempstr = decodeURIComponent(
      str.replace(/\s+/g, '').replace(/[0-9a-f]{2}/g, '%$&'),
    )
  }
  return tempstr
}

export const checkAmountUnit = function (amount: string) {
  if (!Number.isInteger(Number(18))) {
    throw new Error('Decimals must be a integer')
  }
  if (amount === '0') {
    return true
  }
  const amountStr = new BigNumber(amount).toString(10)
  const decimalLen = amountStr.includes('.')
    ? amountStr.length - amountStr.indexOf('.') - 1
    : 0
  return !!new BigNumber(amount).gte(1 / 10 ** 18) && decimalLen <= 18
}

export function isNumber(val: any) {
  const regPos = /^\d+(\.\d+)?$/ // 非负浮点数
  const regNeg =
    /^(-(([0-9]+\.[0-9]*[1-9][0-9]*)|([0-9]*[1-9][0-9]*\.[0-9]+)|([0-9]*[1-9][0-9]*)))$/ // 负浮点数
  return !!(regPos.test(val) || regNeg.test(val))
}

export const checkMaxFeeRate = async function (_: any, value: any) {
  try {
    if (!isNumber(value)) {
      return Promise.reject(new Error('Invalid fee rate'))
    }
    if (value < 0 || value >= 100) {
      return Promise.reject(new Error('Invalid fee rate'))
    }
    if (
      value.toString().split('.')[1] &&
      value.toString().split('.')[1].length > 2
    ) {
      return Promise.reject(new Error('Invalid fee rate'))
    }
    return Promise.resolve()
  } catch {
    return Promise.reject(new Error('Invalid fee rate'))
  }
}

export const wanPubKey2Address = function (
  pubKey: string,
  type: 'pos' | 'osm' = 'pos',
): string {
  const key = Buffer.from(
    pubKey
      .toLowerCase()
      .replace('0x', '')
      .substring(type === 'pos' ? 2 : 0),
    'hex',
  )
  return `0x` + keccak256(key).slice(-40)
}

export const isEmptyObject = (obj: object | null): boolean => {
  if (obj === null) return true
  return Object.keys(obj).length === 0
}

export const getRewardTime = () => {
  const time = new Date(Date.now() + 24 * 3600 * 1000 * 3)
  const utc = Date.UTC(
    time.getUTCFullYear(),
    time.getUTCMonth(),
    time.getUTCDate(),
    8,
  )
  return new Date(utc).toUTCString()
}

export const getUTCTime = (timestamp: string | number | Date) => {
  const time = new Date(timestamp)
  const utc = Date.UTC(
    time.getUTCFullYear(),
    time.getUTCMonth(),
    time.getUTCDate(),
    8,
  )
  return new Date(utc).toUTCString()
}

export const ImgCreator = (iconData: string, iconType: string = 'png') =>
  `data:image/${iconType};base64,${iconData}`

export const handleCopyCode = (elementId: string) => {
  const targetElement = document.getElementById(elementId)
  if (!targetElement) return

  const codeElements = targetElement.querySelectorAll('code')
  const codeContents = Array.from(codeElements).map(
    (code) => code.textContent?.trim() || '',
  )
  const combinedContent = codeContents.join('\n\n')

  navigator.clipboard
    .writeText(combinedContent)
    .then(() => {
      message.success({
        content: 'Code copied to clipboard!',
        className: 'custom-copy',
      })
    })
    .catch(() => {
      message.error('Failed to copy code')
    })
}

export const nextState = (lckEps: number, nxtEps: number): string => {
  if (lckEps === 0) {
    return '1'
  }
  if (nxtEps === 0) {
    return '3'
  } else {
    if (nxtEps === lckEps) {
      return '1'
    } else {
      return '2'
    }
  }
}

export const nextStateString = (stateId: string) => {
  let stateString = ''
  switch (stateId) {
    case '1': {
      stateString = 'N/A'
      break
    }
    case '2': {
      stateString = 'N/A'
      break
    }
    case '3': {
      stateString = 'End Staking'
      break
    }
    default:
      stateString = 'Unknown Status'
      break
  }
  return stateString
}

/**
 * 计算当前时间的下一个月信息
 * @returns {Object} 包含下个月日期、年份和年份状态的对象
 */
export const getNextMonthInfo = () => {
  // 获取当前日期
  const currentDate = new Date()
  const currentYear = currentDate.getUTCFullYear()
  const currentMonth = currentDate.getUTCMonth() // 0-11, 0 = 一月

  // 计算下个月
  let nextMonth = currentMonth + 1
  let nextYear = currentYear
  let yearStatus = 'current' // 'current' 或 'next'

  // 如果当前是12月，下个月将是1月，年份需要+1
  if (nextMonth > 11) {
    nextMonth = 0 // 回到1月
    nextYear = currentYear + 1
    yearStatus = 'next'
  }

  // 创建下个月的日期对象（设置为1号，时间设置为8点）
  const nextMonthDate = new Date(Date.UTC(nextYear, nextMonth, 1, 8, 0, 0))

  // 获取下个月的天数（需要考虑不同月份的天数差异）
  const lastDayOfNextMonth = new Date(
    Date.UTC(nextYear, nextMonth + 1, 0, 8, 0, 0),
  )
  const daysInNextMonth = lastDayOfNextMonth.getUTCDate()

  return {
    nextMonthDate: nextMonthDate.toUTCString(),
    nextMonth: nextMonth + 1, // 转换为1-12的月份数字
    nextYear: nextYear,
    yearStatus: yearStatus,
    currentYear: currentYear,
    currentMonth: currentMonth + 1, // 转换为1-12的月份数字
    daysInNextMonth: daysInNextMonth,
    monthName: nextMonthDate.toLocaleString('en-US', {
      month: 'long',
      timeZone: 'UTC',
    }),
  }
}

/**
 * 简化版本：只返回下个月的日期字符串
 * @returns {String} 下个月的日期字符串
 */
export const getNextMonth = () => {
  const info = getNextMonthInfo()
  return monthfy[info.nextMonth - 1]
}

export const getNextYear = () => {
  const info = getNextMonthInfo()
  return info.nextYear
}
